import { useEffect, useRef, useState } from "react";
import { BatteryFull } from "lucide-react";

// Global camera viewfinder overlay — fixed on top of every section.
// pointer-events-none so it never blocks clicks, high z-index so it's always
// above content and never hidden underneath it.
export default function CameraHUD() {
  const boxRef = useRef(null);
  const [tc, setTc] = useState("00:00:00:00");
  const [active, setActive] = useState(false);

  // Live timecode
  useEffect(() => {
    let frame = 0;
    const start = Date.now();
    const id = setInterval(() => {
      const el = Math.floor((Date.now() - start) / 1000);
      const h = String(Math.floor(el / 3600) % 24).padStart(2, "0");
      const m = String(Math.floor(el / 60) % 60).padStart(2, "0");
      const s = String(el % 60).padStart(2, "0");
      frame = (frame + 1) % 24;
      setTc(`${h}:${m}:${s}:${String(frame).padStart(2, "0")}`);
    }, 1000 / 24);
    return () => clearInterval(id);
  }, []);

  // Cursor-following focus dot — direct 1:1 tracking, no easing lag.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const INTERACTIVE = "a, button, [role='button'], label, select, summary, input, textarea";
    let overInteractive = false;
    const onMove = (e) => {
      if (boxRef.current) {
        boxRef.current.style.transform = `translate3d(${e.clientX}px, ${e.clientY}px, 0) translate(-50%, -50%)`;
      }
      const now = !!(e.target && e.target.closest && e.target.closest(INTERACTIVE));
      if (now !== overInteractive) {
        overInteractive = now;
        setActive(now);
      }
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  return (
    <div
      data-testid="camera-hud"
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-[60] select-none"
    >
      {/* Corner crop marks */}
      <div className="absolute inset-4 sm:inset-6">
        {[
          "left-0 top-0 border-l-2 border-t-2",
          "right-0 top-0 border-r-2 border-t-2",
          "bottom-0 left-0 border-b-2 border-l-2",
          "bottom-0 right-0 border-b-2 border-r-2",
        ].map((c) => (
          <span key={c} className={`absolute h-8 w-8 border-white/60 ${c}`} />
        ))}
      </div>

      {/* REC — top left */}
      <div
        data-testid="hud-rec"
        className="absolute left-6 top-6 flex items-center gap-2 font-hud text-[11px] font-700 uppercase tracking-widest text-[#FF3B30] hud-scan md:left-10"
      >
        <span className="h-2.5 w-2.5 animate-rec-blink rounded-full bg-[#FF3B30]" />
        REC
        <span className="ml-2 hidden text-white/60 sm:inline">TC {tc}</span>
      </div>

      {/* Battery — top right */}
      <div className="absolute right-6 top-6 flex items-center gap-2 font-hud text-[11px] uppercase tracking-widest text-white/70 md:right-10">
        <BatteryFull className="h-4 w-4" />
        100%
      </div>

      {/* Camera settings — bottom */}
      <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between font-hud text-[10px] uppercase tracking-widest text-white/55 hud-scan md:left-10 md:right-10 md:text-[11px]">
        <div className="flex gap-4 md:gap-8">
          <span>ISO 400</span>
          <span>F2.8</span>
          <span>1/50</span>
        </div>
        <span>4K · 60FPS</span>
      </div>

      {/* Cursor-following focus dot — 5×5, instant 1:1 tracking */}
      <div
        ref={boxRef}
        className={`absolute left-0 top-0 rounded-[1px] transition-[background-color,box-shadow,width,height] duration-100 ease-out ${
          active
            ? "h-[14px] w-[14px] bg-[#CBFF3A] shadow-[0_0_18px_rgba(203,255,58,0.75)]"
            : "h-[5px] w-[5px] bg-[#CBFF3A] shadow-[0_0_10px_rgba(203,255,58,0.6)]"
        }`}
      />

    </div>
  );
}
